import { requestsSummaryActivity } from "@/lib/covi/summaryActivityPreference"

import {
  DATA_QUALITY_SECTION_TITLE,
  MAX_SIGNAL_ITEMS,
  MS_PER_DAY,
  TIMEFRAME_DAYS,
  WHAT_CHANGED_SECTION_TITLE,
  WORTH_DIGGING_SECTION_TITLE,
} from "./runsSummaryConstants"
import { buildDetailSummary } from "./runsSummaryDetail"
import { buildExactSimulationDetailSummary } from "./runsSummaryExactDetail"
import { parseRunsSummaryFocusText } from "./runsSummaryFocus"
import {
  formatCount,
  formatRate,
  getRunsSummaryTimeframeLabel,
  pluralize,
} from "./runsSummaryFormat"
import type {
  RunsPageSummary,
  RunsPageSummaryOptions,
  RunsSummaryBucket,
  RunsSummaryTimeframe,
} from "./runsSummaryTypes"
import {
  buildAutoReason,
  buildRunOutcomeDigItems,
  buildSummaryText,
  buildTrend,
  calculateWindowStats,
  getWindowBounds,
  mergeWorthDiggingItems,
  selectAdaptiveRunsSummaryTimeframe,
  shouldPrioritizeRunOutcomes,
} from "./runsSummaryWindow"

export function getRunsPageSummaryInsightSections(summary: RunsPageSummary): {
  changed: string[]
  concerns: string[]
} {
  const worthDigging =
    summary.detailSections.find(
      (section) => section.title === WORTH_DIGGING_SECTION_TITLE,
    )?.items ?? []
  const dataQuality =
    summary.detailSections.find(
      (section) => section.title === DATA_QUALITY_SECTION_TITLE,
    )?.items ?? []

  return {
    changed:
      summary.detailSections.find(
        (section) => section.title === WHAT_CHANGED_SECTION_TITLE,
      )?.items ?? [],
    concerns: [...worthDigging, ...dataQuality],
  }
}

export function buildRunsPageSummary(
  buckets: RunsSummaryBucket[],
  selectedTimeframe: RunsSummaryTimeframe = "auto",
  referenceDate = new Date(),
  options: RunsPageSummaryOptions = {},
): RunsPageSummary {
  const resolvedTimeframe =
    selectedTimeframe === "auto"
      ? selectAdaptiveRunsSummaryTimeframe(buckets, referenceDate)
      : selectedTimeframe
  const windowDays = TIMEFRAME_DAYS[resolvedTimeframe]
  const { startTimeMs: currentStartTimeMs, endTimeMs } = getWindowBounds(
    referenceDate,
    windowDays,
  )
  const previousStartTimeMs = currentStartTimeMs - windowDays * MS_PER_DAY
  const currentStats = calculateWindowStats(
    buckets,
    currentStartTimeMs,
    endTimeMs,
  )
  const previousStats = calculateWindowStats(
    buckets,
    previousStartTimeMs,
    currentStartTimeMs,
  )
  const timeframeLabel = getRunsSummaryTimeframeLabel(resolvedTimeframe)
  const trend = buildTrend(currentStats, previousStats, resolvedTimeframe)
  const autoReason = buildAutoReason(resolvedTimeframe)
  const includeActivity = requestsSummaryActivity(options.focusText)
  const focus = parseRunsSummaryFocusText(options.focusText)
  const detailSummary = options.behaviorInsights
    ? buildExactSimulationDetailSummary(
        options.behaviorInsights,
        options.lookups ?? {},
        focus,
      )
    : buildDetailSummary(
        options.rows ?? [],
        currentStartTimeMs,
        endTimeMs,
        previousStartTimeMs,
        options.lookups ?? {},
        focus,
        includeActivity,
      )
  const prioritizeRunOutcomes =
    includeActivity && shouldPrioritizeRunOutcomes(currentStats)
  const detailSections = mergeWorthDiggingItems(
    detailSummary.sections,
    includeActivity ? buildRunOutcomeDigItems(currentStats) : [],
    prioritizeRunOutcomes,
  )
    .map((section) => ({
      ...section,
      items:
        section.title === WORTH_DIGGING_SECTION_TITLE
          ? section.items
          : section.items.filter((item) => item !== detailSummary.leadSentence),
    }))
    .filter((section) => section.items.length > 0)
  const completionSignal =
    currentStats.finishedRuns > 0
      ? `${formatRate(currentStats.completionRate)} completion across ${formatCount(
          currentStats.finishedRuns,
        )} finished ${pluralize(currentStats.finishedRuns, "run")}`
      : "No finished runs in this window"
  const progressSignal =
    currentStats.inProgressRuns > 0
      ? `${formatCount(currentStats.inProgressRuns)} ${pluralize(
          currentStats.inProgressRuns,
          "run is",
          "runs are",
        )} still in progress`
      : currentStats.unsuccessfulFinishedRuns > 0
        ? `${formatCount(
            currentStats.unsuccessfulFinishedRuns,
          )} unsuccessful finished ${pluralize(
            currentStats.unsuccessfulFinishedRuns,
            "run",
          )}`
        : "No unsuccessful finished runs"

  const runSignalItems = includeActivity
    ? [
        completionSignal,
        progressSignal,
        trend.label,
        ...(selectedTimeframe === "auto" ? [autoReason] : []),
      ]
    : []
  const signalItems = (
    prioritizeRunOutcomes
      ? [...runSignalItems, ...detailSummary.signalItems]
      : [...detailSummary.signalItems, ...runSignalItems]
  ).slice(0, MAX_SIGNAL_ITEMS)

  const runSummaryText = buildSummaryText(
    currentStats,
    trend,
    timeframeLabel,
    null,
  )
  const summaryText = includeActivity
    ? [runSummaryText, detailSummary.leadSentence].filter(Boolean).join(" ")
    : (detailSummary.leadSentence ??
      `Agent performance cannot be assessed for the ${timeframeLabel.toLowerCase()}: no scored agent-evaluation evidence is available.`)

  return {
    selectedTimeframe,
    resolvedTimeframe,
    windowDays,
    timeframeLabel,
    autoReason,
    summaryText,
    signalItems,
    detailSections,
    detailRowCount: detailSummary.detailRowCount,
    currentStats,
  }
}
